import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Progress } from "../../components/ui/progress";
import PremiumLock from "../../components/PremiumLock";
import { 
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  Scale,
  Calendar,
  Camera,
  Target,
  Activity,
  ChevronRight,
  Flame,
  Dumbbell,
  Heart,
  Moon,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Ruler,
  Images,
  LineChart
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mini sparkline chart component
const MiniChart = ({ data, color = "#D4AF37", height = 40 }) => {
  if (!data || data.length < 2) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((val - min) / range) * 80 - 10;
    return `${x},${y}`;
  }).join(" ");
  
  return (
    <svg viewBox="0 0 100 100" className="w-full" style={{ height }} preserveAspectRatio="none">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={100}
        cy={100 - ((data[data.length - 1] - min) / range) * 80 - 10}
        r="3"
        fill={color}
      />
    </svg>
  );
};

// ============================================
// 1. WEIGHT EVOLUTION CHART - Full sized chart
// ============================================
const WeightEvolutionChart = ({ weights, dates }) => {
  if (!weights || weights.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Scale className="w-12 h-12 text-[#333] mb-3" />
        <p className="text-[#666] text-sm">Sin datos de peso todavía</p>
        <p className="text-[#555] text-xs mt-1">Registra tu primer check-in</p>
      </div>
    );
  }
  
  if (weights.length === 1) {
    return (
      <div className="flex flex-col items-center py-6">
        <div className="text-center mb-4">
          <span className="text-3xl font-bold text-[#D4AF37]">{weights[0]}</span>
          <span className="text-lg text-[#888] ml-1">kg</span>
        </div>
        <div className="w-4 h-4 rounded-full bg-[#D4AF37]" />
        <p className="text-xs text-[#666] mt-4 text-center px-4">
          Añade más check-ins para ver tu evolución completa
        </p>
      </div>
    );
  }

  const max = Math.max(...weights);
  const min = Math.min(...weights);
  const range = max - min || 1;
  const padding = range * 0.1;
  
  const chartHeight = 150;
  const chartWidth = 100; // Percentage
  
  const points = weights.map((val, i) => {
    const x = (i / (weights.length - 1)) * chartWidth;
    const y = chartHeight - ((val - min + padding) / (range + padding * 2)) * chartHeight;
    return { x, y, val, date: dates?.[i] };
  });
  
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  // Create gradient area
  const areaD = `${pathD} L ${chartWidth} ${chartHeight} L 0 ${chartHeight} Z`;
  
  return (
    <div className="space-y-3">
      <div className="relative" style={{ height: chartHeight + 40 }}>
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between text-right pr-2">
          <span className="text-[10px] text-[#666]">{max.toFixed(1)}</span>
          <span className="text-[10px] text-[#666]">{((max + min) / 2).toFixed(1)}</span>
          <span className="text-[10px] text-[#666]">{min.toFixed(1)}</span>
        </div>
        
        {/* Chart */}
        <div className="ml-10 h-full">
          <svg 
            viewBox={`0 0 ${chartWidth} ${chartHeight}`} 
            className="w-full" 
            style={{ height: chartHeight }}
            preserveAspectRatio="none"
          >
            {/* Gradient definition */}
            <defs>
              <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
              </linearGradient>
            </defs>
            
            {/* Grid lines */}
            {[0, 25, 50, 75, 100].map(p => (
              <line 
                key={p} 
                x1="0" 
                y1={chartHeight * (p / 100)} 
                x2={chartWidth} 
                y2={chartHeight * (p / 100)} 
                stroke="#2A2A2A" 
                strokeWidth="0.5" 
              />
            ))}
            
            {/* Area fill */}
            <path d={areaD} fill="url(#weightGradient)" />
            
            {/* Line */}
            <path 
              d={pathD} 
              fill="none" 
              stroke="#D4AF37" 
              strokeWidth="2" 
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Data points */}
            {points.map((p, i) => (
              <circle 
                key={i} 
                cx={p.x} 
                cy={p.y} 
                r={i === points.length - 1 ? 4 : 2.5} 
                fill={i === points.length - 1 ? "#D4AF37" : "#1A1A1A"}
                stroke="#D4AF37"
                strokeWidth="1.5"
              />
            ))}
          </svg>
          
          {/* X-axis labels */}
          <div className="flex justify-between mt-2 px-1">
            {dates && dates.length > 0 && (
              <>
                <span className="text-[10px] text-[#666]">
                  {new Date(dates[0]).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                </span>
                {dates.length > 2 && (
                  <span className="text-[10px] text-[#666]">
                    {new Date(dates[Math.floor(dates.length / 2)]).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                  </span>
                )}
                <span className="text-[10px] text-[#666]">
                  {new Date(dates[dates.length - 1]).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Summary */}
      <div className="flex justify-between items-center pt-2 border-t border-[#2A2A2A]">
        <div>
          <span className="text-xs text-[#666]">Inicio: </span>
          <span className="text-sm font-medium text-white">{weights[0]} kg</span>
        </div>
        <div className={cn(
          "px-2 py-1 rounded-full text-xs font-bold",
          weights[weights.length - 1] < weights[0] ? "bg-green-500/20 text-green-400" :
          weights[weights.length - 1] > weights[0] ? "bg-red-500/20 text-red-400" :
          "bg-[#2A2A2A] text-[#888]"
        )}>
          {weights[weights.length - 1] > weights[0] ? '+' : ''}{(weights[weights.length - 1] - weights[0]).toFixed(1)} kg
        </div>
        <div>
          <span className="text-xs text-[#666]">Actual: </span>
          <span className="text-sm font-bold text-[#D4AF37]">{weights[weights.length - 1]} kg</span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// 2. MEASUREMENTS EVOLUTION COMPONENT
// ============================================
const MeasurementsEvolution = ({ checkIns }) => {
  const measurementLabels = {
    waist: { label: 'Cintura', icon: '📏', color: 'text-blue-400' },
    hips: { label: 'Cadera', icon: '🎯', color: 'text-purple-400' },
    chest: { label: 'Pecho', icon: '💪', color: 'text-red-400' },
    arm: { label: 'Brazo', icon: '🦾', color: 'text-green-400' },
    leg: { label: 'Pierna', icon: '🦵', color: 'text-orange-400' }
  };
  
  // Get first and last check-ins with measurements
  const sortedCheckIns = [...checkIns].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  const firstWithMeasurements = sortedCheckIns.find(c => 
    c.measurements && Object.values(c.measurements).some(v => v !== null && v !== undefined && v !== 0)
  );
  
  const lastWithMeasurements = [...sortedCheckIns].reverse().find(c => 
    c.measurements && Object.values(c.measurements).some(v => v !== null && v !== undefined && v !== 0)
  );
  
  if (!firstWithMeasurements || !lastWithMeasurements) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Ruler className="w-12 h-12 text-[#333] mb-3" />
        <p className="text-[#666] text-sm">Sin medidas registradas</p>
        <p className="text-[#555] text-xs mt-1">Añade medidas en tu próximo check-in</p>
      </div>
    );
  }
  
  const isSameCheckIn = firstWithMeasurements.id === lastWithMeasurements.id;
  
  const measurements = Object.keys(measurementLabels).map(key => {
    const first = firstWithMeasurements.measurements?.[key];
    const last = lastWithMeasurements.measurements?.[key];
    const diff = first && last ? (last - first).toFixed(1) : null;
    
    return {
      key,
      ...measurementLabels[key],
      first,
      last,
      diff: diff ? parseFloat(diff) : null,
      hasData: first !== null && first !== undefined && first !== 0
    };
  }).filter(m => m.hasData);
  
  if (measurements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Ruler className="w-12 h-12 text-[#333] mb-3" />
        <p className="text-[#666] text-sm">Sin medidas registradas</p>
        <p className="text-[#555] text-xs mt-1">Añade medidas en tu próximo check-in</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Date range */}
      <div className="flex justify-between items-center text-xs text-[#666]">
        <span>
          {new Date(firstWithMeasurements.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
        </span>
        <span className="text-[#D4AF37]">→</span>
        <span>
          {new Date(lastWithMeasurements.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
        </span>
      </div>
      
      {isSameCheckIn && (
        <p className="text-xs text-[#666] text-center bg-[#1A1A1A] rounded-lg p-2">
          Añade más check-ins con medidas para ver la evolución
        </p>
      )}
      
      {/* Measurement cards */}
      <div className="grid grid-cols-2 gap-2">
        {measurements.map(m => (
          <div 
            key={m.key}
            className="bg-[#1A1A1A] rounded-xl p-3 border border-[#2A2A2A]"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">{m.icon}</span>
              <span className="text-xs text-[#888]">{m.label}</span>
            </div>
            
            <div className="flex items-end justify-between">
              <div>
                <span className="text-lg font-bold text-white">{m.last || m.first}</span>
                <span className="text-xs text-[#666] ml-1">cm</span>
              </div>
              
              {m.diff !== null && !isSameCheckIn && (
                <div className={cn(
                  "px-2 py-0.5 rounded-full text-[10px] font-bold",
                  m.diff < 0 ? "bg-green-500/20 text-green-400" :
                  m.diff > 0 ? "bg-red-500/20 text-red-400" :
                  "bg-[#2A2A2A] text-[#888]"
                )}>
                  {m.diff > 0 ? '+' : ''}{m.diff} cm
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// 3. PHOTO COMPARISON COMPONENT
// ============================================
const PhotoComparison = ({ checkIns }) => {
  const [selectedBefore, setSelectedBefore] = useState(null);
  const [selectedAfter, setSelectedAfter] = useState(null);
  const [viewType, setViewType] = useState('front'); // front, side, back
  
  // Get check-ins with photos
  const checkInsWithPhotos = useMemo(() => 
    checkIns.filter(c => 
      c.photos && (c.photos.front || c.photos.side || c.photos.back)
    ).sort((a, b) => new Date(a.date) - new Date(b.date))
  , [checkIns]);
  
  // Auto-select first and last
  useEffect(() => {
    if (checkInsWithPhotos.length >= 1 && !selectedBefore) {
      setSelectedBefore(checkInsWithPhotos[0]);
    }
    if (checkInsWithPhotos.length >= 2 && !selectedAfter) {
      setSelectedAfter(checkInsWithPhotos[checkInsWithPhotos.length - 1]);
    } else if (checkInsWithPhotos.length === 1 && !selectedAfter) {
      setSelectedAfter(checkInsWithPhotos[0]);
    }
  }, [checkInsWithPhotos, selectedBefore, selectedAfter]);
  
  if (checkInsWithPhotos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Images className="w-12 h-12 text-[#333] mb-3" />
        <p className="text-[#666] text-sm">Sin fotos de progreso</p>
        <p className="text-[#555] text-xs mt-1">Sube fotos en tu próximo check-in</p>
      </div>
    );
  }
  
  const photoTypes = [
    { id: 'front', label: 'Frontal' },
    { id: 'side', label: 'Lateral' },
    { id: 'back', label: 'Espalda' }
  ];
  
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short',
      year: '2-digit'
    });
  };
  
  const beforePhoto = selectedBefore?.photos?.[viewType];
  const afterPhoto = selectedAfter?.photos?.[viewType];
  
  return (
    <div className="space-y-4">
      {/* Photo type tabs */}
      <div className="flex gap-2">
        {photoTypes.map(type => (
          <button
            key={type.id}
            onClick={() => setViewType(type.id)}
            className={cn(
              "flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all",
              viewType === type.id 
                ? "bg-[#D4AF37] text-black" 
                : "bg-[#1A1A1A] text-[#888] hover:bg-[#2A2A2A]"
            )}
          >
            {type.label}
          </button>
        ))}
      </div>
      
      {/* Comparison view */}
      <div className="grid grid-cols-2 gap-3">
        {/* Before */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-[#D4AF37]">ANTES</span>
            {selectedBefore && (
              <span className="text-[10px] text-[#666]">{formatDate(selectedBefore.date)}</span>
            )}
          </div>
          <div className="aspect-[3/4] bg-[#1A1A1A] rounded-xl overflow-hidden border border-[#2A2A2A]">
            {beforePhoto ? (
              <img 
                src={beforePhoto} 
                alt="Antes" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[#444]">
                <Camera className="w-8 h-8 mb-2" />
                <span className="text-[10px]">Sin foto {photoTypes.find(t => t.id === viewType)?.label.toLowerCase()}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* After */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-[#D4AF37]">DESPUÉS</span>
            {selectedAfter && (
              <span className="text-[10px] text-[#666]">{formatDate(selectedAfter.date)}</span>
            )}
          </div>
          <div className="aspect-[3/4] bg-[#1A1A1A] rounded-xl overflow-hidden border border-[#2A2A2A]">
            {afterPhoto ? (
              <img 
                src={afterPhoto} 
                alt="Después" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-[#444]">
                <Camera className="w-8 h-8 mb-2" />
                <span className="text-[10px]">Sin foto {photoTypes.find(t => t.id === viewType)?.label.toLowerCase()}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Check-in selectors */}
      {checkInsWithPhotos.length > 2 && (
        <div className="space-y-3 pt-2 border-t border-[#2A2A2A]">
          <p className="text-xs text-[#666] text-center">Selecciona check-ins para comparar</p>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Before selector */}
            <div>
              <label className="text-[10px] text-[#888] mb-1 block">Antes</label>
              <select
                value={selectedBefore?.id || ''}
                onChange={(e) => {
                  const ci = checkInsWithPhotos.find(c => c.id === e.target.value);
                  setSelectedBefore(ci);
                }}
                className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-2 py-2 text-xs text-white focus:outline-none focus:border-[#D4AF37]"
              >
                {checkInsWithPhotos.map(ci => (
                  <option key={ci.id} value={ci.id}>
                    {formatDate(ci.date)} - S{ci.week}
                  </option>
                ))}
              </select>
            </div>
            
            {/* After selector */}
            <div>
              <label className="text-[10px] text-[#888] mb-1 block">Después</label>
              <select
                value={selectedAfter?.id || ''}
                onChange={(e) => {
                  const ci = checkInsWithPhotos.find(c => c.id === e.target.value);
                  setSelectedAfter(ci);
                }}
                className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg px-2 py-2 text-xs text-white focus:outline-none focus:border-[#D4AF37]"
              >
                {checkInsWithPhotos.map(ci => (
                  <option key={ci.id} value={ci.id}>
                    {formatDate(ci.date)} - S{ci.week}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
      
      {checkInsWithPhotos.length === 1 && (
        <p className="text-xs text-[#666] text-center bg-[#1A1A1A] rounded-lg p-2">
          Sube más fotos en futuros check-ins para ver tu evolución
        </p>
      )}
    </div>
  );
};

// Circular progress component
const CircularProgress = ({ value, max, label, color = "#D4AF37", size = 80 }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const circumference = 2 * Math.PI * 36;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="relative flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size/2}
          cy={size/2}
          r="36"
          fill="none"
          stroke="#2A2A2A"
          strokeWidth="6"
        />
        <circle
          cx={size/2}
          cy={size/2}
          r="36"
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-white">{value}%</span>
      </div>
      <span className="text-[10px] text-[#888] mt-1 text-center">{label}</span>
    </div>
  );
};

// Photo comparison slider
const PhotoSlider = ({ photos, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  if (!photos || photos.length === 0) return null;
  
  const sortedPhotos = [...photos].sort((a, b) => new Date(b.date) - new Date(a.date));
  
  return (
    <div className="space-y-4">
      <div className="relative aspect-[3/4] bg-[#1A1A1A] rounded-xl overflow-hidden">
        {sortedPhotos[currentIndex]?.url ? (
          <img 
            src={sortedPhotos[currentIndex].url} 
            alt="Progress" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#555]">
            <Camera className="w-12 h-12" />
          </div>
        )}
        
        {sortedPhotos.length > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
              disabled={currentIndex === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => setCurrentIndex(i => Math.min(sortedPhotos.length - 1, i + 1))}
              disabled={currentIndex === sortedPhotos.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full disabled:opacity-30"
            >
              <ChevronRightIcon className="w-5 h-5 text-white" />
            </button>
          </>
        )}
        
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
          {sortedPhotos.map((_, i) => (
            <div
              key={i}
              className={cn(
                "w-2 h-2 rounded-full transition-colors",
                i === currentIndex ? "bg-[#D4AF37]" : "bg-white/30"
              )}
            />
          ))}
        </div>
      </div>
      
      {sortedPhotos[currentIndex] && (
        <p className="text-center text-sm text-[#888]">
          {new Date(sortedPhotos[currentIndex].date).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          })}
        </p>
      )}
    </div>
  );
};

const ProgresoPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, isPremium } = useAuth();
  const [checkIns, setCheckIns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("resumen");

  useEffect(() => {
    if (isPremium) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isPremium]);

  const fetchData = async () => {
    try {
      const [checkInsRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/premium/check-ins`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/premium/progress-analytics`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);
      
      // Handle response format: {check_ins: [], total: n}
      const checkInsData = checkInsRes.data?.check_ins || checkInsRes.data || [];
      setCheckIns(Array.isArray(checkInsData) ? checkInsData : []);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error("Error fetching progress data:", error);
      setCheckIns([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate derived data from check-ins
  const progressData = useMemo(() => {
    if (!checkIns || checkIns.length === 0) return null;
    
    const sorted = [...checkIns].sort((a, b) => new Date(a.date) - new Date(b.date));
    const latest = sorted[sorted.length - 1];
    const first = sorted[0];
    const previous = sorted.length > 1 ? sorted[sorted.length - 2] : null;
    
    // Weight data
    const weights = sorted.filter(c => c.weight).map(c => c.weight);
    const weightDates = sorted.filter(c => c.weight).map(c => c.date);
    const weightChange = latest?.weight && first?.weight ? 
      (latest.weight - first.weight).toFixed(1) : 0;
    const weeklyWeightChange = latest?.weight && previous?.weight ?
      (latest.weight - previous.weight).toFixed(1) : 0;
    
    // Adherence averages
    const dietAdherence = sorted.filter(c => c.diet_adherence !== null && c.diet_adherence !== undefined);
    const trainingAdherence = sorted.filter(c => c.training_adherence !== null && c.training_adherence !== undefined);
    const avgDietAdherence = dietAdherence.length > 0 ?
      Math.round(dietAdherence.reduce((sum, c) => sum + c.diet_adherence, 0) / dietAdherence.length) : 0;
    const avgTrainingAdherence = trainingAdherence.length > 0 ?
      Math.round(trainingAdherence.reduce((sum, c) => sum + c.training_adherence, 0) / trainingAdherence.length) : 0;
    
    // Energy and biofeedback trends
    const energyLevels = sorted.filter(c => c.energy_level).map(c => c.energy_level);
    const sleepQuality = sorted.filter(c => c.sleep_quality).map(c => c.sleep_quality);
    
    // Photos
    const allPhotos = [];
    sorted.forEach(c => {
      if (c.photos) {
        ['front', 'side', 'back'].forEach(type => {
          if (c.photos[type]) {
            allPhotos.push({ url: c.photos[type], date: c.date, type });
          }
        });
      }
    });
    
    // Stagnation detection
    const recentWeights = weights.slice(-4);
    const isStagnant = recentWeights.length >= 3 && 
      Math.max(...recentWeights) - Math.min(...recentWeights) < 0.5 &&
      avgDietAdherence > 80;
    
    return {
      latest,
      first,
      previous,
      totalCheckIns: checkIns.length,
      weights,
      weightDates,
      weightChange: parseFloat(weightChange),
      weeklyWeightChange: parseFloat(weeklyWeightChange),
      avgDietAdherence,
      avgTrainingAdherence,
      energyLevels,
      sleepQuality,
      photos: allPhotos,
      isStagnant,
      currentWeight: latest?.weight,
      streak: checkIns.length // Simplified streak
    };
  }, [checkIns]);

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Get trend icon
  const getTrendIcon = (value, inverse = false) => {
    if (value > 0) return inverse ? 
      <ArrowUpRight className="w-4 h-4 text-red-400" /> :
      <ArrowUpRight className="w-4 h-4 text-green-400" />;
    if (value < 0) return inverse ?
      <ArrowDownRight className="w-4 h-4 text-green-400" /> :
      <ArrowDownRight className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-[#888]" />;
  };

  if (!isPremium) {
    return <PremiumLock />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <div className="animate-pulse text-[#D4AF37]">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0B0B0B]/95 backdrop-blur-sm border-b border-[#1A1A1A]">
        <div className="px-4 py-3 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)} 
            className="p-3 -ml-3 text-white hover:text-[#D4AF37] transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            data-testid="progress-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">Mi Progreso</h1>
            <p className="text-xs text-[#888]">
              {progressData ? `${progressData.totalCheckIns} check-ins registrados` : 'Sin datos aún'}
            </p>
          </div>
          <Button
            onClick={() => navigate('/premium/check-in')}
            className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-semibold min-h-[44px]"
            data-testid="new-checkin-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Check-in
          </Button>
        </div>
      </div>

      {/* No data state */}
      {!progressData && (
        <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-20 h-20 rounded-full bg-[#1A1A1A] flex items-center justify-center mb-4">
            <Scale className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Comienza tu seguimiento</h2>
          <p className="text-[#888] text-sm mb-6 max-w-xs">
            Registra tu primer check-in semanal para empezar a ver tu progreso
          </p>
          <Button
            onClick={() => navigate('/premium/check-in')}
            className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-semibold"
          >
            Hacer mi primer check-in
          </Button>
        </div>
      )}

      {/* Main content with data */}
      {progressData && (
        <div className="p-4 space-y-4">
          {/* Hero Stats Cards */}
          <div className="grid grid-cols-2 gap-3">
            {/* Weight Card */}
            <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#2A2A2A] overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Scale className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs text-[#888]">Peso actual</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-white">
                    {progressData.currentWeight || '-'}
                  </span>
                  <span className="text-sm text-[#888] mb-1">kg</span>
                </div>
                {progressData.weights.length > 1 && (
                  <div className="mt-2 h-10">
                    <MiniChart data={progressData.weights} color="#D4AF37" />
                  </div>
                )}
                <div className="flex items-center gap-1 mt-2">
                  {getTrendIcon(progressData.weightChange, true)}
                  <span className={cn(
                    "text-xs font-medium",
                    progressData.weightChange < 0 ? "text-green-400" : 
                    progressData.weightChange > 0 ? "text-red-400" : "text-[#888]"
                  )}>
                    {progressData.weightChange > 0 ? '+' : ''}{progressData.weightChange} kg total
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Streak Card */}
            <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border-[#2A2A2A] overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-xs text-[#888]">Racha</span>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-white">{progressData.streak}</span>
                  <span className="text-sm text-[#888] mb-1">semanas</span>
                </div>
                <p className="text-xs text-[#666] mt-2">
                  {progressData.streak >= 4 ? "¡Excelente constancia!" : "¡Sigue así!"}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* ============================================ */}
          {/* NEW: Weight Evolution Chart Section */}
          {/* ============================================ */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                <LineChart className="w-4 h-4" />
                Evolución del Peso
              </h3>
              <WeightEvolutionChart 
                weights={progressData.weights} 
                dates={progressData.weightDates}
              />
            </CardContent>
          </Card>

          {/* ============================================ */}
          {/* NEW: Measurements Evolution Section */}
          {/* ============================================ */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                <Ruler className="w-4 h-4" />
                Evolución de Medidas
              </h3>
              <MeasurementsEvolution checkIns={checkIns} />
            </CardContent>
          </Card>

          {/* ============================================ */}
          {/* NEW: Photo Comparison Section */}
          {/* ============================================ */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                <Images className="w-4 h-4" />
                Comparador de Fotos
              </h3>
              <PhotoComparison checkIns={checkIns} />
            </CardContent>
          </Card>

          {/* Adherence Section */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Adherencia Media
              </h3>
              <div className="flex justify-around">
                <CircularProgress 
                  value={progressData.avgDietAdherence} 
                  max={100} 
                  label="Dieta"
                  color={progressData.avgDietAdherence >= 80 ? "#22c55e" : progressData.avgDietAdherence >= 60 ? "#D4AF37" : "#ef4444"}
                />
                <CircularProgress 
                  value={progressData.avgTrainingAdherence} 
                  max={100} 
                  label="Entreno"
                  color={progressData.avgTrainingAdherence >= 80 ? "#22c55e" : progressData.avgTrainingAdherence >= 60 ? "#D4AF37" : "#ef4444"}
                />
              </div>
            </CardContent>
          </Card>

          {/* Alerts Section */}
          {progressData.isStagnant && (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-500">Posible estancamiento detectado</p>
                  <p className="text-xs text-[#888] mt-1">
                    Tu peso se ha mantenido estable las últimas semanas con buena adherencia. 
                    Considera hablar con tu coach sobre ajustes.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Biofeedback Section */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Bienestar
              </h3>
              
              {progressData.latest && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#1A1A1A] rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-3 h-3 text-yellow-500" />
                      <span className="text-xs text-[#888]">Energía</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-white">{progressData.latest.energy_level || '-'}</span>
                      <span className="text-xs text-[#666]">/10</span>
                    </div>
                    {progressData.energyLevels.length > 1 && (
                      <div className="h-6 mt-1">
                        <MiniChart data={progressData.energyLevels} color="#eab308" height={24} />
                      </div>
                    )}
                  </div>
                  
                  <div className="p-3 bg-[#1A1A1A] rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Moon className="w-3 h-3 text-blue-400" />
                      <span className="text-xs text-[#888]">Sueño</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-white">{progressData.latest.sleep_quality || '-'}</span>
                      <span className="text-xs text-[#666]">/10</span>
                    </div>
                    {progressData.sleepQuality.length > 1 && (
                      <div className="h-6 mt-1">
                        <MiniChart data={progressData.sleepQuality} color="#60a5fa" height={24} />
                      </div>
                    )}
                  </div>
                  
                  <div className="p-3 bg-[#1A1A1A] rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Heart className="w-3 h-3 text-red-400" />
                      <span className="text-xs text-[#888]">Estrés</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-white">{progressData.latest.stress_level || '-'}</span>
                      <span className="text-xs text-[#666]">/10</span>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-[#1A1A1A] rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Dumbbell className="w-3 h-3 text-green-400" />
                      <span className="text-xs text-[#888]">Hambre</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-white">{progressData.latest.hunger_level || '-'}</span>
                      <span className="text-xs text-[#666]">/10</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Photos Section */}
          {progressData.photos.length > 0 && (
            <Card className="bg-[#121212] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Fotos de Progreso
                </h3>
                <PhotoSlider photos={progressData.photos} />
              </CardContent>
            </Card>
          )}

          {/* Check-in History Timeline */}
          <Card className="bg-[#121212] border-[#2A2A2A]">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Historial de Check-ins
              </h3>
              
              <div className="space-y-3">
                {[...checkIns].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5).map((checkIn, index) => (
                  <div 
                    key={checkIn.id}
                    className="flex items-center gap-3 p-3 bg-[#1A1A1A] rounded-lg"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center">
                      <span className="text-xs font-bold text-[#D4AF37]">S{checkIn.week}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {formatDate(checkIn.date)}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-[#888]">
                        {checkIn.weight && <span>{checkIn.weight} kg</span>}
                        {checkIn.diet_adherence && <span>Dieta: {checkIn.diet_adherence}%</span>}
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] border",
                        checkIn.coach_reviewed 
                          ? "border-green-500/30 text-green-400 bg-green-500/10"
                          : "border-[#2A2A2A] text-[#888]"
                      )}
                    >
                      {checkIn.coach_reviewed ? (
                        <><CheckCircle2 className="w-3 h-3 mr-1" /> Revisado</>
                      ) : "Pendiente"}
                    </Badge>
                  </div>
                ))}
              </div>
              
              {checkIns.length > 5 && (
                <p className="text-center text-xs text-[#666] mt-3">
                  Y {checkIns.length - 5} check-ins más...
                </p>
              )}
            </CardContent>
          </Card>

          {/* Weekly comparison */}
          {progressData.previous && progressData.latest && (
            <Card className="bg-[#121212] border-[#2A2A2A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Comparación Semanal
                </h3>
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-[#1A1A1A] rounded-lg">
                    <p className="text-[10px] text-[#666] mb-1">Peso</p>
                    <p className={cn(
                      "text-sm font-bold",
                      progressData.weeklyWeightChange < 0 ? "text-green-400" :
                      progressData.weeklyWeightChange > 0 ? "text-red-400" : "text-white"
                    )}>
                      {progressData.weeklyWeightChange > 0 ? '+' : ''}{progressData.weeklyWeightChange} kg
                    </p>
                  </div>
                  
                  <div className="p-2 bg-[#1A1A1A] rounded-lg">
                    <p className="text-[10px] text-[#666] mb-1">Energía</p>
                    <p className="text-sm font-bold text-white">
                      {progressData.latest.energy_level || '-'} / {progressData.previous.energy_level || '-'}
                    </p>
                  </div>
                  
                  <div className="p-2 bg-[#1A1A1A] rounded-lg">
                    <p className="text-[10px] text-[#666] mb-1">Adherencia</p>
                    <p className="text-sm font-bold text-white">
                      {progressData.latest.diet_adherence || '-'}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// Plus icon for button
const Plus = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export default ProgresoPage;
